import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SharedFacade,
  ProjectsFacade,
  customEncode,
  customDecode,
  customDecrypt,
  customEncrypt,
} from '@piqet-angular/data';
import { ColDef } from 'ag-grid-community';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { ActionButtonComponent } from 'libs/ui/src/lib/action-button/action-button.component';
import { ProductEolDataGridService } from './product-eol-data-grid.service';
import { AccessPermissionService, CustomModalService, SwalService } from '@piqet-angular/shared';
import { catchError, of, finalize, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ReportsService } from 'libs/data/src/lib/services/report/reports.service';

@Component({
  selector: 'piqet-angular-product-eol',
  templateUrl: './product-eol.component.html',
  styleUrls: ['./product-eol.component.scss'],
})
export class ProductEOLComponent {
  columnDefs!: ColDef[];
  activatedRouteProjectId: any;
  frameworkComponents = {
    actionRenderer: ActionButtonComponent,
  };
  headerData = {
    materialName: 'Product End-of-life',
  };
  activatedRoute: any;
  productId: any;
  productEOLData: any;
  subscription: Subscription;
  deleteProductEOLItem: Subscription;
  menuDataObs!: Subscription;
  menuDataForRouting: any;
  backRouteType: any;
  nextRouteName: any;
  productConfigId: any;
  backBtnLabel: string = 'Next';
  showEdit: boolean = false;

  eolFormGroup!: FormGroup;
  countryList: any = [];
  eolCountryId: any;
  apiResponseData: any; // Store the full API response

  constructor(
    public readonly sharedFacade: SharedFacade,
    public readonly projectFacade: ProjectsFacade,
    public readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly accessService: AccessPermissionService,
    private readonly sharedService: SharedService,
    private readonly projectService: ProjectsService,
    private readonly productEolDataGridService: ProductEolDataGridService,
    private readonly modal: CustomModalService,
    private readonly toaster: ToastrService,
    private readonly formBuilder: FormBuilder,
    private readonly cd: ChangeDetectorRef,
    private readonly swalService: SwalService,
    private readonly reportsService: ReportsService
  ) {
    this.activatedRoute = this.route.snapshot.routeConfig?.path;
    // Don't initialize columns here since we need API data first
    this.route.queryParams.subscribe((values: any) => {
      const url = location.href.split('/');
      this.productConfigId = Number(atob(decodeURIComponent(customDecode(url[6]))));
      const productId = customDecrypt(this.route.snapshot.queryParams['param']);
      this.productId = Number(productId?.id);
      this.getProductEOLList();
    });
    this.subscription = this.productEolDataGridService.editProductEOLData$.subscribe(
      async (editdata: any) => {
        await this.addEditEOLModal(editdata[0], editdata[1]);
      }
    );

    this.deleteProductEOLItem = this.productEolDataGridService.deleteProductEOLData$.subscribe(
      async (deleteData: any) => {
        this.deleteEOL(deleteData[0]);
      }
    );

    this.sharedService.productProjectMenuArrData$.subscribe((res: any) => {
      if (res?.length) {
        this.menuDataForRouting = res.flatMap((product: any) =>
          product.submenu.map((scope: any) => ({
            ...scope,
            productId: product.productId,
            productName: product.name,
          }))
        );
      }
    });
  }

  ngOnInit() {
    this.showEdit = this.accessService.hasPermission('edit', this.activatedRoute);
    this.triggerScroll();

    this.eolFormGroup = this.formBuilder.group({
      country: [null],
    });
  }

  triggerScroll(): void {
    this.sharedService.triggerScroll();
  }

  getProductEOLList() {
    this.projectService.getProductEOLList(this.productId).subscribe((res: any) => {
      this.apiResponseData = res.data;

      this.countryList = res?.data?.countryList ?? [];
      const timestamp = Date.now();
      this.columnDefs = this.productEolDataGridService.productEOLGrid(res.data).map(col => ({
        ...col,
        colId: `${col.colId}_${timestamp}`,
      }));

      // Transform data for grid display
      this.productEOLData =
        res.data?.disposalColumns?.length > 0
          ? this.productEolDataGridService.transformDataForGrid(res.data)
          : [];

      // Set the selected country
      if (this.countryList.length > 0) {
        const selectedCountry = this.countryList.find((x: any) => x.selected);
        if (selectedCountry) {
          this.eolFormGroup.patchValue({
            country: selectedCountry.id,
          });
          this.eolCountryId = selectedCountry.id;
        }
      }

      // Force change detection
      this.cd.detectChanges();
    });
  }

  async addEditEOLModal(id: any, label: any) {
    let response = await this.projectService.getProductEOLFormData(this.productId, id);
    this.modal.invoke('addProductEOL', {
      initialState: {
        newEOLData: response.data,
        label: label,
        countryId: this.eolCountryId,
        productEOLData: this.productEOLData,
      },
      class: 'modal-xl',
    });
    this.modal.ref?.content.saveProductEOL.subscribe((saveProductEOL: any) => {
      this.addEditEOL(saveProductEOL);
    });
  }

  addEditEOL(data: any) {
    this.projectService
      .addEditProductEOL(data)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        ),
        finalize(() => {
          this.modal.close();
          this.getProductEOLList();
        })
      )
      .subscribe(res => {
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
      });
  }

  deleteEOL(data: any) {
    this.projectService
      .deleteProductEOL(data.id)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        ),
        finalize(() => {
          this.getProductEOLList();
        })
      )
      .subscribe(res => {
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
      });
  }

  backRoute() {
    this.findPrevRoute();
    this.router.navigate(
      [`projects/product-configuration/${this.productConfigId}/${this.backRouteType}`],
      { queryParams: { param: customEncrypt(this.productId) } }
    );
  }

  findPrevRoute() {
    const activeIndex = this.menuDataForRouting.findIndex(
      (x: any) => x.description == this.activatedRoute
    );
    const nextIndex = activeIndex - 1;
    this.backRouteType =
      nextIndex < 0 ? 'configuration' : this.menuDataForRouting[nextIndex]?.description;
  }

  findNextRoute() {
    const currentProductId = this.productId; // Fetch this from route or localStorage
    const activeIndex = this.menuDataForRouting.findIndex(
      (x: any) => x.description === this.activatedRoute && x.productId === currentProductId
    );
    // Find the next index and route
    const nextIndex = activeIndex + 1;
    if (
      nextIndex < this.menuDataForRouting.length &&
      this.menuDataForRouting[nextIndex].productId === currentProductId
    ) {
      this.nextRouteName = this.menuDataForRouting[nextIndex].description;
    } else {
      this.nextRouteName = null; // No next route
    }
    this.backBtnLabel = 'Next';
  }

  async nextRoute() {
    this.findNextRoute();
    const activeProductProjectId: any = localStorage.getItem('productConfigId');
    this.sharedService.accessProductSideBarData('charts');
    const reportFormData: any = await this.reportsService.checkReportFromDataForProduct(
      activeProductProjectId
    );
    if (reportFormData && reportFormData.data) {
      const { item1, item2 } = reportFormData.data;

      if (item1 === false) {
        if (item2) {
          await this.swalService.confirmNew('Report validation', item2, undefined, undefined, true);
        }
        return;
      } else {
        this.router.navigate([
          `projects/product-configuration/${customEncode(
            btoa(activeProductProjectId)
          )}/chart-at-glance`,
        ]);
      }
    }
  }

  ngOnDestroy() {
    this.deleteProductEOLItem.unsubscribe();
    this.subscription.unsubscribe();
  }

  changeCountry(event: any) {
    if (!event?.id) return;
    this.eolCountryId = event.id;
    if (this.productEOLData.length && this.eolCountryId) {
      this.projectService
        .updateProductEndOfLifeCountry(this.productId, this.eolCountryId)
        .subscribe((res: any) => {
          if (!res.messageType) {
            // TO do
          } else {
            this.toaster.error(res.message);
          }
        });
    }
  }
}
